import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { TennisBall, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      await authAPI.recoverPassword(email, redirectTo);
      setSent(true);
    } catch {
      // Backend returns 200 even for non-existent emails to prevent enumeration;
      // still, show a generic success on any response to be safe.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fbfaf8]">
      <div className="absolute inset-0 h-[45vh]">
        <img
          src="https://images.unsplash.com/photo-1719760825481-90f57e905ccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHx0ZW5uaXMlMjBjb3VydCUyMGdyYXNzfGVufDB8fHx8MTc3NjIwMzAzOHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-[#fbfaf8]" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-end pb-12 px-6" style={{ minHeight: '100vh' }}>
        <div className="mb-auto pt-16 text-center">
          <div className="inline-flex items-center gap-2">
            <TennisBall size={28} weight="duotone" className="text-white" />
            <span className="font-serif text-2xl text-white tracking-tight">The Rippers Club</span>
          </div>
        </div>

        <div className="w-full max-w-sm bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] shadow-xl p-6 space-y-5" data-testid="forgot-password-page">
          <div className="text-center">
            <h1 className="font-serif text-3xl text-[#013523] tracking-tight">Forgot password?</h1>
            <p className="text-sm text-[#6a766e] mt-1">We'll email you a reset link.</p>
          </div>

          {sent ? (
            <div className="space-y-4" data-testid="forgot-password-sent">
              <div className="bg-[#015e3f]/10 text-[#013523] text-sm rounded-xl px-4 py-4 border border-[#015e3f]/20">
                If an account exists for <span className="font-medium">{email}</span>, a recovery link has been sent. Check your inbox and open the link on this device to set a new password.
              </div>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3 font-medium transition-transform active:scale-95"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-1.5 block">Email</label>
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-[#e3dcd1] rounded-xl px-4 py-3 text-[#2a332c] focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] transition-all"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#015e3f] text-white rounded-full px-6 py-3 font-medium transition-transform active:scale-95 hover:bg-[#014d34] disabled:opacity-50"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}

          <Link
            to="/login"
            className="flex items-center justify-center gap-1 text-sm text-[#6a766e] hover:text-[#013523]"
            data-testid="back-to-login"
          >
            <ArrowLeft size={14} weight="bold" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
